import React, { useState } from 'react';
import { Marker, Popup } from 'react-map-gl/mapbox';
import { getDynamicMapElementZIndex, MapElement } from '../../../../types/ui/mapElement';
import { Text } from '../../../../components/general/Text';
import type { MissionId } from '../../../../types/gcs/mission';
import {
  VehicleStatusCategory,
  type VehicleStatusCategory as VehicleStatusCategoryType
} from '../../../../types/ui/vehicleStatusCategory';

interface VehicleMarkerProps {
  gcsId: string;
  missionId: MissionId;
  vehicleLabel?: string;
  longitude: number;
  latitude: number;
  altitude: number;
  rotation: number;
  vehicleCategory: VehicleStatusCategoryType | null;
  hasPackage: boolean;
  isUnderMyControl: boolean;
  isConnected: boolean;
  markerSize: number;
  onClick?: () => void;
}

const getVehicleCategoryColor = (vehicleCategory: VehicleStatusCategoryType | null): string => {
  switch (vehicleCategory) {
    case VehicleStatusCategory.INFLIGHT_FAILURE_MODE:
      return 'var(--map-element-inflight-failure-mode)';
    case VehicleStatusCategory.INFLIGHT_NOMINAL:
      return 'var(--map-element-inflight-nominal)';
    case VehicleStatusCategory.PREFLIGHT:
      return 'var(--map-element-preflight)';
    case VehicleStatusCategory.POSTFLIGHT:
      return 'var(--map-element-postflight)';
    case VehicleStatusCategory.ON_GROUND:
    default:
      return 'var(--map-element-on-ground)';
  }
};

const getVehicleFillColor = (vehicleCategory: VehicleStatusCategoryType | null, isConnected: boolean): string => {
  if (!isConnected) {
    return '#E3E3E3';
  }

  return getVehicleCategoryColor(vehicleCategory);
};

const getVehicleStrokeColor = (vehicleCategory: VehicleStatusCategoryType | null, isConnected: boolean): string => {
  if (isConnected) {
    return 'white';
  }

  return getVehicleCategoryColor(vehicleCategory);
};

export const VehicleMarker: React.FC<VehicleMarkerProps> = ({
  gcsId,
  missionId,
  vehicleLabel,
  longitude,
  latitude,
  altitude,
  rotation,
  vehicleCategory,
  hasPackage,
  isUnderMyControl,
  isConnected,
  markerSize,
  onClick
}) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const fillColor = getVehicleFillColor(vehicleCategory, isConnected);
  const strokeColor = getVehicleStrokeColor(vehicleCategory, isConnected);
  const packageIndicatorWidth = markerSize / 12;
  const packageIndicatorHeight = markerSize / 12;
  const packageIndicatorY = 20 - packageIndicatorHeight / 2;
  const packageHandleRadius = packageIndicatorWidth / 4;
  const opacity = isUnderMyControl ? 1 : 0.5;
  const markerZIndex = getDynamicMapElementZIndex(MapElement.VEHICLE, altitude);

  return (
    <>
      <Marker
        key={`vehicle-${gcsId}-${missionId}`}
        longitude={longitude}
        latitude={latitude}
        anchor='center'
        rotation={rotation}
        onClick={(event) => {
          event.originalEvent.stopPropagation();
          setIsPopupOpen((prev) => !prev);
          onClick?.();
        }}
        style={{
          zIndex: markerZIndex,
          cursor: 'pointer'
        }}
      >
        <svg
          width={markerSize}
          height={markerSize}
          viewBox='0 0 40 40'
          style={{
            cursor: 'pointer',
            opacity
          }}
        >
          {/* Navigation arrow pointing up (will be rotated by yaw) */}
          <path
            d='M20 4 Q20.8 4 21.2 4.8 L32.8 30.9 Q33.6 32.8 31.6 32.8 L21.8 27.9 Q20 27 18.2 27.9 L8.4 32.8 Q6.4 32.8 7.2 30.9 L18.8 4.8 Q19.2 4 20 4 Z'
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth='2'
            strokeLinejoin='round'
            strokeLinecap='round'
            vectorEffect='non-scaling-stroke'
          />

          {/* Package indicator */}
          {hasPackage && (
            <>
              <circle
                cx={20}
                cy={packageIndicatorY}
                r={packageHandleRadius}
                fill='none'
                stroke={strokeColor}
                strokeWidth='0.5'
              />
              <rect
                x={20 - packageIndicatorWidth / 2}
                y={packageIndicatorY}
                width={packageIndicatorWidth}
                height={packageIndicatorHeight}
                rx={packageIndicatorWidth / 4}
                fill={strokeColor}
              />
            </>
          )}
        </svg>
      </Marker>
      {isPopupOpen && vehicleLabel && (
        <Popup
          longitude={longitude}
          latitude={latitude}
          anchor='bottom'
          offset={32}
          className='marker-popup'
          closeButton={true}
          closeOnClick={true}
          onClose={() => setIsPopupOpen(false)}
          style={{
            zIndex: markerZIndex
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <Text variant='body2'>{vehicleLabel}</Text>
          </div>
        </Popup>
      )}
    </>
  );
};
